import React from 'react';
import { Flex, Text, Avatar, VStack } from '@chakra-ui/react';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { formatTimestamp } from './config-helpers';

export interface ConfigChangeProps {
  change: {
    configurationCurrentValue: string;
    configurationPreviousValue?: string;
    description?: string;
    userName?: string;
    createdDate: string;
    tenantId: string;
    jiratTicket?: string;
    jiraPurpose?: string;
    serviceLineName?: string;
    keyname?: string;
    section?: string;
  };
  userDetails?: {
    username: string;
    firstName: string;
    lastName: string;
  } | null;
}

const ConfigChange: React.FC<ConfigChangeProps> = ({ change, userDetails }) => {
  const formattedDate = formatTimestamp(change.createdDate);

  const getInitials = () => {
    if (!userDetails?.firstName && !userDetails?.lastName) {
      return '?';
    }

    const firstInitial = userDetails.firstName
      ? userDetails.firstName.charAt(0).toUpperCase()
      : '';
    const lastInitial = userDetails.lastName
      ? userDetails.lastName.charAt(0).toUpperCase()
      : '';

    return `${firstInitial}${lastInitial}` || '?';
  };

  const getChangedByText = () => {
    if (!userDetails) {
      return 'No user information available';
    }

    const fullName = `${userDetails.firstName || ''} ${userDetails.lastName ||
      ''}`.trim();
    const username = userDetails.username;

    if (fullName && username) {
      return `Changed by: ${fullName} ${username}`;
    } else if (fullName) {
      return `Changed by: ${fullName}`;
    } else if (username) {
      return `Changed by: ${username}`;
    } else {
      return 'No user information available';
    }
  };

  const initials = getInitials();

  return (
    <Flex
      borderWidth="1px"
      borderRadius="lg"
      p={4}
      shadow="sm"
      align="start"
      gap={4}
      wrap="wrap">
      <VStack align="start" spacing={1} flex="1">
        {/* Avatar + Old->New Value*/}
        <Flex direction="row" align="center" gap={3}>
          <Avatar
            size="sm"
            bg="gray.200"
            name={initials}
            color="black"></Avatar>
          <Flex align="center" mt={2}>
            <Text fontSize="sm">{change.configurationPreviousValue}</Text>
            <ArrowForwardIcon mx={2} />
            <Text fontSize="sm">{change.configurationCurrentValue}</Text>
          </Flex>
        </Flex>

        {/* Changed by information */}
        <Text fontSize="sm" color="gray.600">
          {getChangedByText()}
        </Text>

        {/* Details */}
        <Text fontSize="sm">Keyname: {change.keyname}</Text>
        <Text fontSize="sm">Section: {change.section}</Text>
        <Text fontSize="sm">
          Service line: {change.serviceLineName === 'Inpatient Professional Coding'
            ? 'Bedside Pro'
            : (change.serviceLineName || 'Global')}
        </Text>

        {change.jiratTicket && (
          <Text fontSize="sm">JIRA Ticket #: {change.jiratTicket}</Text>
        )}

        {change.jiraPurpose && (
          <Text fontSize="sm">Description: {change.jiraPurpose}</Text>
        )}

        <Text fontSize="sm" color="gray.500">
          {formattedDate}
        </Text>
      </VStack>
    </Flex>
  );
};

export default ConfigChange;
